import {
  addDays,
  addMonths,
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths
} from 'date-fns'

import {
  SEARCH_DATE_BASIS,
  isClosingDateBasis,
  type IncomeSearchDateBasis
} from '@/features/shared/models'
import { type ShipmentDeliveryStatusType } from '@/libs/api/types'
import { filterObjectEmpty } from '@/libs/utils'

import { type ShipmentOrdersSearch } from './shipment-orders-search'

/**
 * 配送依頼一覧の検索フォームの値
 */
export type ShipmentOrdersSearchFormValue = {
  page: number
  /** 配送ステータス (複数選択) */
  status: ShipmentDeliveryStatusType[]
  /** 配送依頼No  */
  orderNo: string
  /** 発送日 From */
  shippingDateFrom: Date | null
  /** 発送日 To */
  shippingDateTo: Date | null
  /** 着日 From */
  deliveryDateFrom: Date | null
  /** 着日 To */
  deliveryDateTo: Date | null
  /** 登録日 From（#5285 VOC_PJ-506） */
  createdAtFrom: Date | null
  /** 登録日 To */
  createdAtTo: Date | null
  /** 検索日付基準（通常 / 締日基準・請求月）。VOC_PJ-939 #4387 */
  searchDateBasis: IncomeSearchDateBasis
  /** 締日基準（請求月）モード時の対象年月。DEFAULT 時は送信しない */
  operatingYearMonth: Date | null
  /** 荷主ID */
  customerIds: number[] | null
  /** パートナーID **/
  partnerIds: number[] | null
  /** 配送種別ID */
  shipmentCategoryIds: number[] | null
  /** 発地 地点ID */
  originSiteIds: number[] | null
  /** 着地 地点ID */
  destSiteIds: number[] | null
  /** 車種ID */
  truckModelIds: number[] | null
  /** 配送依頼作成方法
   * - true: 手入力のみ, false: インポートのみ, null: すべて
   */
  isManual: boolean | null
  /** 配車パートナーID **/
  dispatchPartnerIds: number[] | null
  /** 配車運送会社ID **/
  dispatchSubPartnerIds: number[] | null
  /** 配車車両番号 **/
  dispatchTruckCode: string
  /** カスタムキー横断フリーワード検索（customKey1〜5 のいずれかに部分一致。VOC_PJ-960 #4487） */
  customKeyFreeWord: string
}

/**
 * 配送依頼一覧の検索フォームの初期値
 */
export const defaultShipmentOrdersSearchFormValue: ShipmentOrdersSearchFormValue =
  {
    page: 1,
    status: [],
    orderNo: '',
    shippingDateFrom: null,
    shippingDateTo: null,
    deliveryDateFrom: null,
    deliveryDateTo: null,
    createdAtFrom: null,
    createdAtTo: null,
    searchDateBasis: SEARCH_DATE_BASIS.DEFAULT,
    operatingYearMonth: null,
    partnerIds: null,
    customerIds: null,
    shipmentCategoryIds: null,
    originSiteIds: null,
    destSiteIds: null,
    truckModelIds: null,
    isManual: null,
    dispatchTruckCode: '',
    dispatchPartnerIds: null,
    dispatchSubPartnerIds: null,
    customKeyFreeWord: ''
  }

/**
 * 配送依頼一覧の検索条件から検索フォームの値に変換する
 */
export const mapToShipmentOrdersSearchFormValue = ({
  shippingDateFrom,
  shippingDateTo,
  deliveryDateFrom,
  deliveryDateTo,
  createdAtFrom,
  createdAtTo,
  searchDateBasis,
  operatingYearMonth,
  status,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  includeDispatched,
  ...rest
}: ShipmentOrdersSearch): ShipmentOrdersSearchFormValue => ({
  ...defaultShipmentOrdersSearchFormValue,
  ...rest,
  status: status ?? [],
  shippingDateFrom: shippingDateFrom ? parseISO(shippingDateFrom) : null,
  shippingDateTo: shippingDateTo ? parseISO(shippingDateTo) : null,
  deliveryDateFrom: deliveryDateFrom ? parseISO(deliveryDateFrom) : null,
  deliveryDateTo: deliveryDateTo ? parseISO(deliveryDateTo) : null,
  createdAtFrom: createdAtFrom ? parseISO(createdAtFrom) : null,
  createdAtTo: createdAtTo ? parseISO(createdAtTo) : null,
  searchDateBasis: searchDateBasis ?? SEARCH_DATE_BASIS.DEFAULT,
  operatingYearMonth: operatingYearMonth ? parseISO(operatingYearMonth) : null
})

/** 発送日の検索期間の上限（月）。#4567 の Biz 回答 2-B で運行実績一覧に揃えた */
export const SHIPPING_DATE_MAX_MONTHS = 12

/**
 * 発送日の期間が上限（SHIPPING_DATE_MAX_MONTHS ヶ月）以内かどうかを判定する
 * @param from 開始日
 * @param to 終了日
 * @returns 上限以内であればtrue
 *
 * 例:
 * - 2026/01/01 ~ 2027/01/01 → true (OK)
 * - 2026/01/01 ~ 2027/01/02 → false (NG)
 * - 2026/01/15 ~ 2027/01/15 → true (OK)
 * - 2026/01/15 ~ 2027/01/16 → false (NG)
 */
export const isShippingDateRangeWithinMaxMonths = (
  from: Date,
  to: Date
): boolean => {
  // 開始日に上限月数を加算した日付
  const limitDate = addMonths(startOfDay(from), SHIPPING_DATE_MAX_MONTHS)
  // 終了日が上限日以前であればOK
  return (
    isBefore(startOfDay(to), limitDate) ||
    startOfDay(to).getTime() === limitDate.getTime()
  )
}

/**
 * 発送日バリデーション結果
 */
export type ShippingDateValidationResult =
  | { isValid: true }
  | { isValid: false; errorType: 'required' | 'reversed' | 'exceeded' }

/**
 * 発送日のバリデーションを行う
 *
 * 締日基準検索（#4387）では計上月が母集合を絞るため、`isRequired: false` を渡して
 * 必須チェックだけを外す。BE 側の shouldValidateShippingDate と同じ規則で、
 * 片側だけの指定はレンジとして不完全なので required、両方指定なら上限を効かせる。
 *
 * @param shippingDateFrom 発送日（開始日）
 * @param shippingDateTo 発送日（終了日）
 * @param options.isRequired From/To の指定を必須とするか（既定 true）
 * @returns バリデーション結果
 */
export const validateShippingDateRange = (
  shippingDateFrom: Date | null,
  shippingDateTo: Date | null,
  options?: { isRequired?: boolean }
): ShippingDateValidationResult => {
  const isRequired = options?.isRequired ?? true

  // 必須が外れているときに限り、両方未指定を「レンジ指定なし」として通す
  if (!isRequired && !shippingDateFrom && !shippingDateTo) {
    return { isValid: true }
  }

  // 必須チェック
  if (!shippingDateFrom || !shippingDateTo) {
    return { isValid: false, errorType: 'required' }
  }

  // 逆転チェック（#4568）。上限チェックより先に見る。
  // 逆転したまま上限判定に入ると「12ヶ月以内で指定してください」という
  // 的外れなメッセージになるため
  if (isAfter(startOfDay(shippingDateFrom), startOfDay(shippingDateTo))) {
    return { isValid: false, errorType: 'reversed' }
  }

  // 期間上限チェック
  if (!isShippingDateRangeWithinMaxMonths(shippingDateFrom, shippingDateTo)) {
    return { isValid: false, errorType: 'exceeded' }
  }

  return { isValid: true }
}

/** 登録日の検索期間の上限（月）。発送日（#4567）と同じ 12ヶ月に揃えた（#5285 VOC_PJ-506） */
export const CREATED_AT_MAX_MONTHS = 12

/**
 * 片側だけ指定された登録日を CREATED_AT_MAX_MONTHS か月幅で閉じる（#5285 §4.2-6）。
 *
 * 両側あり／両側なしはそのまま返す。BE は登録日を両側必須で検証するため、片側指定の
 * URL をそのまま送ると 400 になる。補完を FE に置くことで、利用者は補完後の値を
 * 画面と URL で確認できる。
 *
 * 補完幅は validateCreatedAtRange の上限判定と同じ境界（From + 12ヶ月 を含む）に合わせて
 * あるため、補完した値が自分の上限チェックで弾かれることはない。
 */
export const complementCreatedAtRange = (
  from: Date | null,
  to: Date | null
): { from: Date | null; to: Date | null } => {
  if (from && !to) {
    return { from, to: subDays(addMonths(from, CREATED_AT_MAX_MONTHS), 1) }
  }
  if (!from && to) {
    return { from: addDays(subMonths(to, CREATED_AT_MAX_MONTHS), 1), to }
  }
  return { from, to }
}

/**
 * 登録日バリデーション結果。
 *
 * 発送日と違い required は持たない。片側指定は complementCreatedAtRange が閉じるため、
 * 検証時点では「両側あり」か「両側なし」のどちらかになる
 */
export type CreatedAtValidationResult =
  | { isValid: true }
  | { isValid: false; errorType: 'reversed' | 'exceeded' }

/**
 * 登録日のバリデーションを行う。
 *
 * 登録日は任意条件なので、レンジとして閉じていない（両側なし・片側だけ残っている）
 * 場合は絞り込まない指定として通す。判定順は発送日（validateShippingDateRange）と同じで、
 * 逆転を先に見る。逆転したまま上限判定に入ると「12ヶ月以内で指定してください」という
 * 的外れなメッセージになるため
 *
 * @param createdAtFrom 登録日（開始日）
 * @param createdAtTo 登録日（終了日）
 */
export const validateCreatedAtRange = (
  createdAtFrom: Date | null,
  createdAtTo: Date | null
): CreatedAtValidationResult => {
  if (!createdAtFrom || !createdAtTo) {
    return { isValid: true }
  }

  if (isAfter(startOfDay(createdAtFrom), startOfDay(createdAtTo))) {
    return { isValid: false, errorType: 'reversed' }
  }

  const limitDate = addMonths(startOfDay(createdAtFrom), CREATED_AT_MAX_MONTHS)
  const isWithinMaxMonths =
    isBefore(startOfDay(createdAtTo), limitDate) ||
    startOfDay(createdAtTo).getTime() === limitDate.getTime()
  if (!isWithinMaxMonths) {
    return { isValid: false, errorType: 'exceeded' }
  }

  return { isValid: true }
}

/**
 * 配送依頼一覧の検索フォームの値を検索条件に変換する。
 *
 * 締日基準モード（請求月）では検索基準と計上月を加えて送る。
 * 暦日レンジ（発送日/着日）は併用でき、指定されていれば AND で絞り込まれる
 * （Q-8 = 8-A、改訂 (10)）。
 */
export const mapToShipmentOrdersSearch = ({
  shippingDateFrom,
  shippingDateTo,
  deliveryDateFrom,
  deliveryDateTo,
  createdAtFrom,
  createdAtTo,
  searchDateBasis,
  operatingYearMonth,
  ...formValue
}: ShipmentOrdersSearchFormValue): ShipmentOrdersSearch => {
  const { status, ...rest } = formValue
  const isClosingDate = isClosingDateBasis(searchDateBasis)

  return filterObjectEmpty({
    ...rest,
    page: 1,
    status: status.length > 0 ? status : undefined,
    searchDateBasis: isClosingDate ? searchDateBasis : undefined,
    operatingYearMonth:
      isClosingDate && operatingYearMonth
        ? format(startOfMonth(operatingYearMonth), 'yyyy-MM-dd')
        : undefined,
    shippingDateFrom: shippingDateFrom
      ? format(shippingDateFrom, 'yyyy-MM-dd')
      : undefined,
    shippingDateTo: shippingDateTo
      ? format(shippingDateTo, 'yyyy-MM-dd')
      : undefined,
    deliveryDateFrom: deliveryDateFrom
      ? format(deliveryDateFrom, 'yyyy-MM-dd')
      : undefined,
    deliveryDateTo: deliveryDateTo
      ? format(deliveryDateTo, 'yyyy-MM-dd')
      : undefined,
    createdAtFrom: createdAtFrom
      ? format(createdAtFrom, 'yyyy-MM-dd')
      : undefined,
    createdAtTo: createdAtTo ? format(createdAtTo, 'yyyy-MM-dd') : undefined
  })
}
